import jwt_decode from "jwt-decode";
import { logout } from "../auth/auth";
import {
  PricingType,
  SetupType,
} from "../../models/features/Integration/DmsSetup";
import { DmsPart } from "../../models/features/ShoppingPortal/DmsPart";
import { GridColDef, GridColumnVisibilityModel } from "@mui/x-data-grid-pro";
import { useSelector } from "react-redux";
import { AppState } from "../../redux/reducer/rootReducer";
import SellRoundedIcon from "@mui/icons-material/SellRounded";
import LooksOneOutlinedIcon from "@mui/icons-material/LooksOneOutlined";
import LooksTwoOutlinedIcon from "@mui/icons-material/LooksTwoOutlined";
import Looks3OutlinedIcon from "@mui/icons-material/Looks3Outlined";
import Looks4OutlinedIcon from "@mui/icons-material/Looks4Outlined";
import Looks5OutlinedIcon from "@mui/icons-material/Looks5Outlined";
import { Chip, Tooltip } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import { PortalSettingRule } from "../../models/features/ShopifySetting/Portal Setting Rules/PortalSettingRules";
import dayjs from "dayjs";

export function isTokenValid(token: string, dispatch: any) {
  var decoder = jwt_decode(token) as any;
  var expiry = new Date(1000 * decoder.exp);
  var now = new Date(Date.now());

  if (expiry <= now) {
    dispatch(logout());
    return false;
  } else {
    return true;
  }
}

export const getPartPrice = (
  item: DmsPart,
  pricing: PricingType | undefined,
) => {
  switch (pricing) {
    case PricingType.Price:
      return item.price;
    case PricingType.MSRP:
      if (pricing == PricingType.MSRP && item.suggestedRetailPrice > 0)
        return item.suggestedRetailPrice;
      return item.price;
    default:
      return item.price;
  }
};

export function getFullName(
  firstName?: string,
  middleName?: string,
  lastName?: string,
) {
  let fullName = firstName;
  if (middleName) {
    fullName += " " + middleName;
  }
  if (lastName) {
    fullName += " " + lastName;
  }
  return fullName;
}

export function GetAppGridColumns(
  columns: GridColDef[],
  visibility?: GridColumnVisibilityModel,
) {
  return columns.map((column) => ({
    ...column,
    hide: visibility?.[column.field] === false,
  }));
}

export const handleGetPartPrice = (
  part: DmsPart,
  rules: PortalSettingRule[] | undefined,
) => {
  let price = part.price.toFixed(2);
  if (!rules || rules.length === 0) {
    return price;
  }
  let priceText = null;
  const shouldHidePrice = rules.some((rule) => {
    if (rule.hidePrice) {
      if (rule.applyOnAllParts) {
        priceText = rule.priceReplacementText || "Call for pricing";
        return true;
      }
      if (
        rule.manufacturers?.includes(part.mfrCode) ||
        rule.partNumbers?.includes(part.inventoryPartId)
      ) {
        priceText = rule.priceReplacementText || "Call for pricing";
        return true;
      }
    }
    return false;
  });

  return shouldHidePrice ? priceText : price;
};

export const handleCartButtonAction = (
  part: DmsPart,
  rules: PortalSettingRule[] | undefined,
) => {
  if (!rules || rules.length === 0) {
    return { hideButton: false, tooltipText: "" };
  }

  let tooltipText = "Call for details";
  const hideButton = rules.some((rule: PortalSettingRule) => {
    if (rule.hideCartButton) {
      if (rule.applyOnAllParts) {
        tooltipText = rule.cartReplacementText || tooltipText;
        return true;
      }
      if (
        rule.manufacturers?.includes(part.mfrCode) ||
        rule.partNumbers?.includes(part.inventoryPartId)
      ) {
        tooltipText = rule.cartReplacementText || tooltipText;
        return true;
      }
    }
    return false;
  });

  return { hideButton, tooltipText };
};

export function capitalizeFirstLetter(str: string | undefined) {
  if (!str) return ""; // Check for empty or undefined string
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}
export const isValueNullOrZero = (value: any) => !value;

export const getUniqueNameValidation = ({
  name,
  existingNames = [],
  excludeName,
  duplicateMessage = "A filter with this name already exists. Please choose a different name.",
}: {
  name: string;
  existingNames?: string[];
  excludeName?: string;
  duplicateMessage?: string;
}) => {
  const normalize = (value: string) => value.toLowerCase().trim();
  const normalizedName = normalize(name);
  const excludedName = excludeName ? normalize(excludeName) : undefined;
  const isDuplicate =
    normalizedName !== "" &&
    existingNames.some((existingName) => {
      const normalizedExistingName = normalize(existingName);
      return !excludedName || normalizedExistingName !== excludedName
        ? normalizedExistingName === normalizedName
        : false;
    });

  return {
    isDuplicate,
    showDuplicateError: normalizedName !== "" && isDuplicate,
    helperText: isDuplicate ? duplicateMessage : "",
  };
};

export const isInvalidFilterNameToken = (name: string) => {
  const normalizedName = name.trim().toLowerCase();
  return (
    normalizedName === "null" ||
    normalizedName === '""' ||
    normalizedName === "''"
  );
};

export const handleGetProductPrice = (
  product: any,
  priceType?: PricingType | keyof typeof PricingType,
) => {
  var selectedPrice = 0;
  const enumPriceType =
    typeof priceType === "string"
      ? PricingType[priceType as keyof typeof PricingType]
      : priceType;
  if (product?.aggregatePrices !== null) {
    switch (enumPriceType) {
      case PricingType.Price:
        selectedPrice =
          product?.aggregatePrices?.price !== null
            ? product?.aggregatePrices?.price
            : product?.price;
        break;
      case PricingType.MSRP:
        selectedPrice = product?.aggregatePrices?.suggestedRetailPrice;
        break;
      case PricingType.PriceLevel1:
        selectedPrice = product.aggregatePrices.priceLevel1;
        break;
      case PricingType.PriceLevel2:
        selectedPrice = product.aggregatePrices.priceLevel2;
        break;
      case PricingType.PriceLevel3:
        selectedPrice = product.aggregatePrices.priceLevel3;
        break;
      case PricingType.PriceLevel4:
        selectedPrice = product.aggregatePrices.priceLevel4;
        break;
      case PricingType.PriceLevel5:
        selectedPrice = product.aggregatePrices.priceLevel5;
        break;
      default:
        console.log("Invalid price type", priceType);
    }
  }
  if (isValueNullOrZero(selectedPrice)) {
    selectedPrice = product?.price ?? 0;
  }
  return selectedPrice.toFixed(2);
};

export function usePriceOptions(): {
  type: PricingType;
  label: string;
  key: string;
}[] {
  const locations = useSelector(
    (state: AppState) => state.authReducer.dmsSetup?.dmsLocations ?? [],
  );
  return [
    { type: PricingType.Price, label: "MSRP", key: "price" },
    {
      type: PricingType.PriceLevel1,
      label: locations[0]?.dmsType === 0 ? "Web Price 1" : "Price Level 1",
      key: "priceLevel1",
    },
    {
      type: PricingType.PriceLevel2,
      label: locations[0]?.dmsType === 0 ? "Web Price 2" : "Price Level 2",
      key: "priceLevel2",
    },
    {
      type: PricingType.PriceLevel3,
      label: locations[0]?.dmsType === 0 ? "Web Price 3" : "Price Level 3",
      key: "priceLevel3",
    },
    {
      type: PricingType.PriceLevel4,
      label: locations[0]?.dmsType === 0 ? "Web Price 4" : "Price Level 4",
      key: "priceLevel4",
    },
    {
      type: PricingType.PriceLevel5,
      label: locations[0]?.dmsType === 0 ? "Web Price 5" : "Price Level 5",
      key: "priceLevel5",
    },
  ];
}
export const getSyncedVariantData = (row: any) => {
  let variant = row.externalProduct.variants.find(
    (n: any) => n.id.toString() === row.variantId,
  );

  if (variant === undefined) {
    variant = row.externalProduct.variants[0];
  }
  return variant;
};

export const generatePriceIcons = (
  priceType?: PricingType,
  priceTag?: string,
) => {
  switch (PricingType[priceType?.toString() as keyof typeof PricingType]) {
    case PricingType.Price:
      return (
        <Tooltip title="This product is using MSRP.">
          <SellRoundedIcon color="warning" fontSize="small" />
        </Tooltip>
      );
    case PricingType.PriceLevel1:
      return (
        <Tooltip title={`This product is using ${priceTag} 1.`}>
          <LooksOneOutlinedIcon color="success" fontSize="small" />
        </Tooltip>
      );
    case PricingType.PriceLevel2:
      return (
        <Tooltip title={`This product is using ${priceTag} 2.`}>
          <LooksTwoOutlinedIcon color="success" fontSize="small" />
        </Tooltip>
      );
    case PricingType.PriceLevel3:
      return (
        <Tooltip title={`This product is using ${priceTag} 3.`}>
          <Looks3OutlinedIcon color="success" fontSize="small" />
        </Tooltip>
      );
    case PricingType.PriceLevel4:
      return (
        <Tooltip title={`This product is using ${priceTag} 4.`}>
          <Looks4OutlinedIcon color="success" fontSize="small" />
        </Tooltip>
      );
    case PricingType.PriceLevel5:
      return (
        <Tooltip title={`This product is using ${priceTag} 5.`}>
          <Looks5OutlinedIcon color="success" fontSize="small" />
        </Tooltip>
      );
  }
};
export const formatDate = (serverDate: any) => {
  if (serverDate) {
    const utcDate = serverDate.endsWith("Z") ? serverDate : `${serverDate}Z`;
    const date = new Date(utcDate);

    return date.toLocaleString(undefined, {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }
  return "";
};

export const calculateGrossMargin = (cost: number, price: number) => {
  if (price > 0) {
    const grossMargin = ((price - cost) / price) * 100;
    return grossMargin.toFixed(2);
  } else {
    return "N/A";
  }
};
export const PRICING_WARNING_ICON = (
  <Tooltip title="The selected pricing level could not be found, MSRP has been used instead.">
    <WarningIcon color="error" fontSize="small" />
  </Tooltip>
);
export const getStoreProductStatusChip = (status: string) => {
  if (status.toLowerCase() === "active")
    return <Chip label="Active" color="success" size="small" />;
  else return <Chip label="Draft" color="error" size="small" />;
};
export const getProductTypeChip = (type: string) => {
  if (type === "Wholegoods") {
    return <Chip label="Wholegood" color="success" size="small" />;
  } else if (type === "Parts") {
    return <Chip label="Part" color="warning" size="small" />;
  } else if (type === "NewArrival") {
    return <Chip label="Virtual Showroom" color="primary" size="small" />;
  } else if (type === "UsedProduct") {
    return <Chip label="Used Product" color="info" size="small" />;
  } else return <Chip label={type} color="error" size="small" />;
};

export const getProductRedirectUrl = (
  siteDomain: string,
  handle: string,
  setupType: SetupType,
): string => {
  switch (setupType) {
    case SetupType.ShopifyLegacy:
    case SetupType.Shopify:
      return `${siteDomain}/products/${handle}`;
    case SetupType.BigCommerce:
      return `${siteDomain}${handle}`;
    case SetupType.WordPress:
      return `${handle}`;
    default:
      return `${siteDomain}/products/${handle}`;
  }
};
export const setupTypeLabelMap: Record<SetupType, string> = {
  [SetupType.Shopify]: "Shopify",
  [SetupType.ShopifyLegacy]: "Shopify",
  [SetupType.HiImpact]: "HighImpact",
  [SetupType.BigCommerce]: "BigCommerce",
  [SetupType.WordPress]: "WordPress",
};

/**
 * Filter columns based on setup type
 * @param columns - Array of GridColDef columns
 * @param setupType - The setup type (Shopify, BigCommerce, HiImpact, etc.)
 * @returns Filtered columns array
 */
export const filterColumnsBySetupType = (
  columns: GridColDef[],
  setupType: SetupType,
): GridColDef[] => {
  switch (setupType) {
    case SetupType.HiImpact:
      // For HiImpact, exclude catalog-related columns
      return columns.filter(
        (col) =>
          col.field !== "catalogProduct.sku" &&
          col.field !== "type" &&
          col.field !== "title" &&
          col.field !== "enableCatalogSync" &&
          col.field !== "modelNumber" &&
          col.field !== "storeStock" &&
          col.field !== "outOfStock" &&
          col.field !== "disableStockSync" &&
          col.field !== "disablePriceSync" &&
          col.field !== "status" &&
          col.field !== "isExistingProduct" &&
          col.field !== "actions" &&
          col.field !== "UsedProduct.actions",
      );
    case SetupType.WordPress:
    case SetupType.BigCommerce:
      // For BigCommerce, exclude outOfStock and enableCatalogSync columns
      return columns.filter(
        (col) =>
          col.field !== "outOfStock" &&
          col.field !== "enableCatalogSync" &&
          col.field !== "autoPublish",
      );

    case SetupType.Shopify:
    case SetupType.ShopifyLegacy:
    default:
      // For Shopify and other types, return all columns
      return columns;
  }
};
export const IsShopifySetup = (setupType?: SetupType): boolean => {
  return (
    setupType === SetupType.Shopify || setupType === SetupType.ShopifyLegacy
  );
};

export const round2 = (n: number) =>
  Math.round((n + Number.EPSILON) * 100) / 100;

export const dealerTzToUtcDate = (
  value: string | Date | null | undefined,
  ianaId: string,
): Date | null => {
  if (!value) return null;
  const d = dayjs.tz(String(value), ianaId).utc();
  return d.isValid() ? d.toDate() : null;
};

export const getCalculatedSalePrice = (
  price: number,
  salePrice: number,
  discountValue: number,
): number => {
  if (salePrice > 0) return salePrice;
  if (discountValue < 0) {
    return round2(price * (1 + discountValue));
  }
  return 0.0;
};
